import { MarqueeIndexedFixtureView } from '@cds/betting-offer/domain-specific';
import { MarqueeTile } from '@frontend/sports/types/components/content';
import { BetStationWidgetLayoutResponse, Widget, WidgetLayoutResponse, WidgetLocation } from '@frontend/sports/types/components/widget';
import { MarqueePayload } from '@frontend/sports/types/components/widget/types';
import { cloneDeep, isEqual, lastIndexOf, sortBy } from 'lodash-es';

export function forSlot(model: WidgetLayoutResponse | BetStationWidgetLayoutResponse, slot: WidgetLocation): Widget<unknown>[] {
    return model?.widgets?.filter((config) => config.location === slot) || [];
}

export const LdWidgetConfigFlagKey = 'WidgetPreferenceOnLeaguePage';

export interface LdWidgetConfigModel {
    quickLinksEnabled: boolean;
    scoreboardCarouselEnabled: boolean;
    competitionIds: number[];
}

export function mergeMarqueePayload(regularMarqueePayload: MarqueePayload, personalizedMarqueePayload: MarqueePayload): MarqueePayload {
    const { combinedTiles, combinedFixtures } = getCombinedTilesAndFixtures(regularMarqueePayload, personalizedMarqueePayload);
    let orderedTiles = cloneDeep(combinedTiles);
    let orderedFixtures = cloneDeep(combinedFixtures);

    if (!personalizedMarqueePayload.isPersonalizedMarquee && personalizedMarqueePayload.tiles?.length) {
        orderedTiles = sortBy(combinedTiles, (tile) => {
            const sitecoreTemplateId = tile.trackingDetail.sitecoreTemplateId
                ? tile.trackingDetail.sitecoreTemplateId.slice(Math.max(0, lastIndexOf(tile.trackingDetail.sitecoreTemplateId, '/') + 1))
                : '';
            return personalizedMarqueePayload.sitecoreIds.indexOf(sitecoreTemplateId);
        });

        orderedTiles.forEach((data, index) => {
            const combinedTileIndex = combinedTiles.findIndex((tile) => isEqual(tile, data));
            const combinedFixtureIndex = combinedFixtures.findIndex((fixture) => fixture.requestIndex === combinedTileIndex);
            if (combinedFixtureIndex !== -1) {
                orderedFixtures[combinedFixtureIndex].requestIndex = index;
            }
        });

        orderedFixtures = sortBy(orderedFixtures, (fixture) => fixture.requestIndex);
    }

    return {
        backgroundContainerConfig: regularMarqueePayload.backgroundContainerConfig || personalizedMarqueePayload.backgroundContainerConfig,
        fixtures: orderedFixtures,
        globalLink: regularMarqueePayload.globalLink || personalizedMarqueePayload.globalLink,
        isPersonalizedMarquee: regularMarqueePayload.isPersonalizedMarquee || personalizedMarqueePayload.isPersonalizedMarquee,
        showAutomatedMarqueesOnEventPage:
            regularMarqueePayload.showAutomatedMarqueesOnEventPage || personalizedMarqueePayload.showAutomatedMarqueesOnEventPage,
        tiles: orderedTiles,
        sitecoreIds: [],
    };
}

function getCombinedTilesAndFixtures(
    regularMarqueePayload: MarqueePayload,
    personalizedMarqueePayload: MarqueePayload,
): { combinedTiles: MarqueeTile[]; combinedFixtures: MarqueeIndexedFixtureView[] } {
    const combinedFixtures = [...(regularMarqueePayload.fixtures ?? [])];
    const combinedTiles = [...(regularMarqueePayload.tiles ?? [])];

    personalizedMarqueePayload.tiles?.forEach((data, index) => {
        const personalizedFixtureIndex = personalizedMarqueePayload.fixtures.findIndex((x) => x.requestIndex === index);
        combinedTiles.push(data);
        if (personalizedFixtureIndex !== -1) {
            combinedFixtures.push({ ...personalizedMarqueePayload.fixtures[personalizedFixtureIndex], requestIndex: combinedTiles.length - 1 });
        }
    });

    return { combinedTiles, combinedFixtures };
}
